import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Fade,
  Chip,
  alpha,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useApp from "../../store/contexts/AppContext";

// Keyframe animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 4px 16px rgba(212, 68, 1, 0.4);
  }
  50% {
    box-shadow: 0 4px 24px rgba(212, 68, 1, 0.8), 0 0 30px rgba(212, 68, 1, 0.5);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Styled components
const AdContainer = styled(Card)(({ theme }) => ({
  position: "relative",
  borderRadius: 20,
  overflow: "hidden",
  marginBottom: theme.spacing(3),
  backgroundColor: alpha(theme.palette.common.white, 0.03),
  backdropFilter: "blur(30px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: `0 10px 40px ${alpha(theme.palette.common.black, 0.4)}`,
  animation: `${fadeIn} 0.8s cubic-bezier(0.4, 0, 0.2, 1)`,
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
  },
}));

const AdBadge = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: 16,
  left: 16,
  backgroundColor: alpha(theme.palette.primary.main, 0.95),
  color: theme.palette.common.white,
  fontSize: "0.7rem",
  fontWeight: 700,
  height: 28,
  zIndex: 3,
  backdropFilter: "blur(10px)",
  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  animation: `${glow} 2s ease-in-out infinite`,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 12,
  right: 12,
  backgroundColor: alpha(theme.palette.common.black, 0.7),
  color: theme.palette.common.white,
  zIndex: 4,
  width: 36,
  height: 36,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.black, 0.9),
    transform: "rotate(90deg) scale(1.15)",
  },
}));

const AdImageWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 280,
  overflow: "hidden",
  borderRadius: "20px 20px 0 0",
  background: `linear-gradient(180deg, transparent 0%, ${alpha(
    theme.palette.common.black,
    0.85
  )} 100%)`,
}));

const AdImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "scale(1.1)",
  },
});

const AdVideo = styled("video")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const VideoOverlay = styled(Box)(({ playing }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  opacity: playing ? 0 : 1,
  transition: "opacity 0.3s ease",
  cursor: "pointer",
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  width: 70,
  height: 70,
  backgroundColor: alpha(theme.palette.primary.main, 0.9),
  color: theme.palette.common.white,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    transform: "scale(1.2)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "2.5rem",
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  animation: `${slideUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both`,
}));

const AdTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: 800,
  marginBottom: theme.spacing(1.5),
  color: theme.palette.common.white,
  letterSpacing: "-0.01em",
  lineHeight: 1.3,
}));

const AdDescription = styled(Typography)(({ theme }) => ({
  fontSize: "0.95rem",
  color: alpha(theme.palette.common.white, 0.75),
  marginBottom: theme.spacing(2.5),
  lineHeight: 1.6,
}));

const CTAButton = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  borderRadius: 12,
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: theme.palette.common.white,
  fontSize: "1rem",
  fontWeight: 700,
  cursor: "pointer",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.3
    )}, transparent)`,
    transition: "left 0.6s ease",
  },
  "&:hover": {
    transform: "translateY(-3px) scale(1.05)",
    boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.6)}`,
    "&::before": {
      left: "100%",
    },
  },
  "&:active": {
    transform: "translateY(-1px) scale(1.02)",
  },
}));

const DotsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(1),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1),
}));

const DotIndicator = styled(Box)(({ active, theme }) => ({
  width: active ? 28 : 10,
  height: 10,
  borderRadius: 5,
  backgroundColor: active
    ? theme.palette.primary.main
    : alpha(theme.palette.common.white, 0.3),
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  boxShadow: active
    ? `0 0 12px ${alpha(theme.palette.primary.main, 0.6)}`
    : "none",
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.dark
      : alpha(theme.palette.common.white, 0.5),
    transform: "scale(1.2)",
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 400,
  borderRadius: 20,
  overflow: "hidden",
  backgroundColor: alpha(theme.palette.common.white, 0.03),
  animation: `${shimmer} 2s ease-in-out infinite`,
  background: `linear-gradient(
    90deg,
    ${alpha(theme.palette.common.white, 0.03)} 0%,
    ${alpha(theme.palette.common.white, 0.08)} 50%,
    ${alpha(theme.palette.common.white, 0.03)} 100%
  )`,
  backgroundSize: "1000px 100%",
}));

const AdBanner = ({
  position = "hero",
  maxAds = 1,
  autoRotate = true,
  interval = 10000,
}) => {
  const {
    appState: { userData },
    getActiveAdsApiCall,
    recordAdViewApiCall,
    recordAdClickApiCall,
  } = useApp();

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [viewRecorded, setViewRecorded] = useState(new Set());
  const videoRef = useRef(null);
  const fetchInitiatedRef = useRef(false);
  const hasFetchedRef = useRef(false); // NEW: Track if we've already fetched

  // Fetch active ads - CRITICAL: Remove all dependencies except the ones we pass
  const fetchAds = useCallback(async () => {
    if (fetchInitiatedRef.current || hasFetchedRef.current) return;

    fetchInitiatedRef.current = true;
    hasFetchedRef.current = true;

    try {
      setLoading(true);
      const result = await getActiveAdsApiCall();

      if (result.success && result.data) {
        const adsData = Array.isArray(result.data)
          ? result.data
          : result.data.ads || [];

        const userAccountType = (
          userData?.account_type || "basic"
        ).toLowerCase();

        const filteredAds = adsData.filter((ad) => {
          const visiblePlans = ad.visible_to_plans || [
            "basic",
            "standard",
            "premium",
            "elite",
          ];
          return visiblePlans.includes(userAccountType);
        });

        setAds(filteredAds.slice(0, maxAds));
      }
    } catch (error) {
      console.error("Error fetching ads:", error);
    } finally {
      setLoading(false);
      fetchInitiatedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // EMPTY ARRAY - fetch once only

  // Record ad view
  const recordAdView = useCallback(
    async (adId) => {
      if (viewRecorded.has(adId)) return;

      try {
        const result = await recordAdViewApiCall(adId);
        if (result.success) {
          setViewRecorded((prev) => new Set([...prev, adId]));
        }
      } catch (error) {
        console.error("Error recording ad view:", error);
      }
    },
    [viewRecorded, recordAdViewApiCall]
  );

  // Record ad click
  const handleAdClick = async (ad) => {
    try {
      await recordAdClickApiCall(ad.id);

      if (ad.click_url) {
        window.open(ad.click_url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error recording ad click:", error);
    }
  };

  // Auto-rotate ads
  useEffect(() => {
    if (autoRotate && ads.length > 1 && !dismissed) {
      const timer = setInterval(() => {
        setCurrentAdIndex((prev) => (prev + 1) % ads.length);
      }, interval);

      return () => clearInterval(timer);
    }
  }, [autoRotate, ads.length, interval, dismissed]);

  // Record view when ad becomes visible
  useEffect(() => {
    if (ads.length > 0 && !dismissed && ads[currentAdIndex]) {
      const timer = setTimeout(() => {
        recordAdView(ads[currentAdIndex].id);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [currentAdIndex, ads, dismissed, recordAdView]);

  // Initial fetch - ONLY once when component mounts
  useEffect(() => {
    if (userData?.id) {
      fetchAds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]); // NO fetchAds in dependencies!

  // Toggle video playback
  const toggleVideo = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  };

  if (loading) {
    return <LoadingContainer />;
  }

  if (dismissed || ads.length === 0) {
    return null;
  }

  const currentAd = ads[currentAdIndex];

  return (
    <Fade in={!dismissed} timeout={600}>
      <AdContainer elevation={0}>
        <AdBadge label="Sponsored" />
        <CloseButton onClick={() => setDismissed(true)}>
          <CloseIcon />
        </CloseButton>

        <AdImageWrapper onClick={() => handleAdClick(currentAd)}>
          {currentAd.ad_type === "video" && currentAd.media?.[0] ? (
            <>
              <AdVideo
                ref={videoRef}
                src={currentAd.media[0].file_path}
                loop
                muted
                playsInline
              />
              <VideoOverlay playing={videoPlaying} onClick={toggleVideo}>
                <PlayButton>
                  {videoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </PlayButton>
              </VideoOverlay>
            </>
          ) : currentAd.media?.[0] ? (
            <AdImage
              src={currentAd.media[0].file_path}
              alt={currentAd.title}
              loading="lazy"
            />
          ) : null}
        </AdImageWrapper>

        <ContentWrapper>
          <AdTitle variant="h5">{currentAd.title}</AdTitle>
          {currentAd.description && (
            <AdDescription>{currentAd.description}</AdDescription>
          )}
          {currentAd.click_url && (
            <CTAButton onClick={() => handleAdClick(currentAd)}>
              <span>Learn More</span>
              <OpenInNewIcon fontSize="small" />
            </CTAButton>
          )}
        </ContentWrapper>

        {ads.length > 1 && (
          <DotsContainer>
            {ads.map((_, index) => (
              <DotIndicator
                key={index}
                active={index === currentAdIndex}
                onClick={() => setCurrentAdIndex(index)}
              />
            ))}
          </DotsContainer>
        )}
      </AdContainer>
    </Fade>
  );
};

export default AdBanner;
